import React, { useState, useEffect, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Form } from 'antd';
import dayjs from 'dayjs';
import { StyledDetailedDeliveryForm } from './style';
import { Label } from 'ui/Label';
import { DetailedDeliveryCard } from 'ui/Card/DetailedDeliveryCard';
import {
  ListingProduct,
  DetailedDeliveryData,
  DetailedDeliveryFormProps,
  DetailedDeliveryFormValues,
  UnusedProduct,
  SplitDeliveryListingProduct,
} from '../../../../types/ListingData';
import { DetailedDeliveryFormContent } from './DetailedDeliveryFormContent';

export const DetailedDeliveryForm = ({
  onFormStateChange,
  onDeliveriesChange,
  existingDeliveries = [],
  availableProducts = [],
  splitDeliveriesMode,
}: DetailedDeliveryFormProps) => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const [deliveries, setDeliveries] = useState<DetailedDeliveryData[]>([]);

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingDelivery, setEditingDelivery] =
    useState<DetailedDeliveryData | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<ListingProduct[]>(
    []
  );
  const [isAllProducts, setIsAllProducts] = useState(false);
  const [isAllQuantities, setIsAllQuantities] = useState(false);
  const [selectedApplicationId, setSelectedApplicationId] = useState<
    number | null
  >(null);

  const handleApplicationChange = useCallback(
    (applicationId: number | null) => {
      setSelectedApplicationId(applicationId);
      splitDeliveriesMode?.onApplicationChange?.(applicationId);
    },
    [splitDeliveriesMode]
  );

  const calculateUnusedProducts = (
    excludeDelivery?: DetailedDeliveryData,
    showTotalQuantities = false
  ) => {
    const unusedProducts: UnusedProduct[] = [];

    availableProducts.forEach((product) => {
      const productName =
        product.name ||
        product.category?.name ||
        `Product ${product.category_id}`;

      const totalUsedQuantity = deliveries.reduce((total, delivery) => {
        if (excludeDelivery && delivery === excludeDelivery) {
          return total;
        }

        if (
          splitDeliveriesMode?.enabled &&
          selectedApplicationId &&
          !showTotalQuantities
        ) {
          const deliveryApplicationId = delivery.selectedApplicationId;
          if (deliveryApplicationId !== selectedApplicationId) {
            return total;
          }
        }

        const productInDelivery =
          delivery.products?.find(
            (p) =>
              (p.category_id && p.category_id === product.category_id) ||
              (p.listing_product_id && p.listing_product_id === product.id)
          ) ||
          delivery.selected_products?.find(
            (p) =>
              p.category_id === product.category_id ||
              (p.id && p.id === product.id)
          );

        if (productInDelivery) {
          let productUsed: number;
          if (delivery.is_full_quantity) {
            if ('delivery_quantity' in productInDelivery) {
              productUsed = parseFloat(
                productInDelivery.delivery_quantity?.toString() || '0'
              );
            } else {
              const raw =
                delivery.product_quantities?.[product.category_id!] ??
                (product.id
                  ? delivery.product_quantities?.[product.id]
                  : undefined);
              productUsed = parseFloat(String(raw ?? 0)) || 0;
            }
          } else {
            if ('delivery_quantity' in productInDelivery) {
              productUsed = parseFloat(
                productInDelivery.delivery_quantity?.toString() || '0'
              );
            } else {
              const raw =
                delivery.product_quantities?.[product.category_id!] ??
                (product.id
                  ? delivery.product_quantities?.[product.id]
                  : undefined);
              productUsed = parseFloat(String(raw ?? 0)) || 0;
            }
          }
          return total + productUsed;
        }
        return total;
      }, 0);

      let maxAvailableQuantity = product.required_quantity;
      if (
        splitDeliveriesMode?.enabled &&
        selectedApplicationId &&
        !showTotalQuantities
      ) {
        const extendedProduct = product as SplitDeliveryListingProduct;
        const selectedApp = extendedProduct._applications?.find(
          (app: {
            applicationId: number;
            applicationProductId: number;
            quantity: number;
            offeredPrice: number;
            farmName?: string;
            applicationName?: string;
          }) => app.applicationId === selectedApplicationId
        );
        if (selectedApp) {
          maxAvailableQuantity = selectedApp.quantity;
        }
      }

      const remainingQuantity = maxAvailableQuantity - totalUsedQuantity;

      if (remainingQuantity > 0) {
        unusedProducts.push({
          name: productName,
          quantity: remainingQuantity,
          unit:
            product.storage_unit?.short || product.storage_unit?.name || 'kg.',
        });
      }
    });

    return unusedProducts;
  };

  useEffect(() => {
    setDeliveries(existingDeliveries || []);
    setShowAddForm(false);
    setIsEditMode(false);
    setEditingDelivery(null);
    setSelectedProducts([]);
    setIsAllProducts(false);
    setIsAllQuantities(false);
    setSelectedApplicationId(null);
    form.resetFields();
  }, [existingDeliveries]);

  const getAvailableProducts = (includeDelivery?: DetailedDeliveryData) => {
    const unusedProducts = calculateUnusedProducts(includeDelivery);
    const available = availableProducts.filter((product) => {
      const productName =
        product.name ||
        product.category?.name ||
        `Product ${product.category_id}`;
      const unusedProduct = unusedProducts.find(
        (unused) => unused.name === productName
      );
      return unusedProduct && unusedProduct.quantity > 0;
    });

    if (includeDelivery) {
      const deliveryCategoryIds = new Set<number>();

      includeDelivery.selected_products?.forEach((p) => {
        if (p.category_id) deliveryCategoryIds.add(p.category_id);
      });
      includeDelivery.products?.forEach((p) => {
        if (p.category_id) {
          deliveryCategoryIds.add(p.category_id);
        } else if (p.id) {
          const product = availableProducts.find((ap) => ap.id === p.id);
          if (product?.category_id) {
            deliveryCategoryIds.add(product.category_id);
          }
        }
      });

      availableProducts.forEach((product) => {
        if (
          product.category_id &&
          deliveryCategoryIds.has(product.category_id)
        ) {
          const alreadyIncluded = available.some(
            (p) => p.category_id === product.category_id
          );
          if (!alreadyIncluded) {
            available.push(product);
          }
        }
      });
    }

    return available;
  };

  const handleAllProductsChange = (checked: boolean) => {
    setIsAllProducts(checked);
    form.setFieldValue('is_all_products', checked);

    if (checked) {
      let availableProductsOnly =
        isEditMode && editingDelivery
          ? getAvailableProducts(editingDelivery)
          : getAvailableProducts();

      if (splitDeliveriesMode?.enabled && selectedApplicationId) {
        availableProductsOnly = availableProductsOnly.filter((product) => {
          const extendedProduct = product as SplitDeliveryListingProduct;
          return extendedProduct._applications?.some(
            (app: {
              applicationId: number;
              applicationProductId: number;
              quantity: number;
              offeredPrice: number;
              farmName?: string;
              applicationName?: string;
            }) => app.applicationId === selectedApplicationId
          );
        });
      }

      setSelectedProducts(availableProductsOnly);
      const categoryIds = availableProductsOnly
        .map((p) => p.category_id)
        .filter((id): id is number => id !== undefined && id !== null);

      const selectValue = categoryIds.map((categoryId) => {
        const product = availableProductsOnly.find(
          (p) => p.category_id === categoryId
        );
        return {
          value: categoryId,
          label: product?.name || product?.category?.name || '',
        };
      });

      form.setFieldsValue({
        selected_products: selectValue,
        product_quantities: {},
      });

      if (isAllQuantities) {
        const unusedProducts =
          isEditMode && editingDelivery
            ? calculateUnusedProducts(editingDelivery)
            : calculateUnusedProducts();
        const newQuantities: { [categoryId: number]: number } = {};

        availableProductsOnly.forEach((product) => {
          if (product.category_id) {
            const unusedProduct = unusedProducts.find((unused) => {
              const productName =
                product.name ||
                product.category?.name ||
                `Product ${product.category_id}`;
              return unused.name === productName;
            });
            newQuantities[product.category_id] = unusedProduct?.quantity || 0;
          }
        });

        form.setFieldValue('product_quantities', newQuantities);
      }
    } else {
      setSelectedProducts([]);
      setIsAllQuantities(false);
      form.setFieldValue('selected_products', []);
      form.setFieldValue('product_quantities', {});
      form.setFieldValue('is_all_products', false);
      form.setFieldValue('is_full_quantity', false);
    }
  };

  const handleAllQuantitiesChange = (checked: boolean) => {
    setIsAllQuantities(checked);
    form.setFieldValue('is_full_quantity', checked);

    if (checked && selectedProducts.length > 0) {
      const unusedProducts =
        isEditMode && editingDelivery
          ? calculateUnusedProducts(editingDelivery)
          : calculateUnusedProducts();

      const newQuantities: { [categoryId: number]: number } = {};

      selectedProducts.forEach((product) => {
        if (product.category_id) {
          const unusedProduct = unusedProducts.find((unused) => {
            const productName =
              product.name ||
              product.category?.name ||
              `Product ${product.category_id}`;
            return unused.name === productName;
          });

          newQuantities[product.category_id] = unusedProduct?.quantity || 0;
        }
      });

      form.setFieldsValue({ product_quantities: newQuantities });
    } else if (!checked) {
      form.setFieldsValue({ product_quantities: {} });
    }
  };

  const handleProductSelectionChange = (categoryIds: number[]) => {
    let availableProductsOnly =
      isEditMode && editingDelivery
        ? getAvailableProducts(editingDelivery)
        : getAvailableProducts();

    if (splitDeliveriesMode?.enabled && selectedApplicationId) {
      availableProductsOnly = availableProductsOnly.filter((product) => {
        const extendedProduct = product as SplitDeliveryListingProduct;
        return extendedProduct._applications?.some(
          (app: {
            applicationId: number;
            applicationProductId: number;
            quantity: number;
            offeredPrice: number;
            farmName?: string;
            applicationName?: string;
          }) => app.applicationId === selectedApplicationId
        );
      });
    }

    const selected = availableProductsOnly.filter(
      (p) => p.category_id && categoryIds.includes(p.category_id)
    );
    setSelectedProducts(selected);

    const selectValue = selected
      .map((p) => ({
        value: p.category_id,
        label: p.name || p.category?.name || '',
      }))
      .filter((item) => item.value !== undefined && item.value !== null);

    form.setFieldValue('selected_products', selectValue);

    const allAvailableCategoryIds = Array.from(
      new Set(
        availableProductsOnly
          .map((p) => p.category_id)
          .filter((id): id is number => id !== undefined && id !== null)
      )
    ).sort();

    const selectedCategoryIdsSorted = [...categoryIds].sort();

    const allProductsSelected =
      allAvailableCategoryIds.length > 0 &&
      allAvailableCategoryIds.length === selectedCategoryIdsSorted.length &&
      allAvailableCategoryIds.every(
        (id, index) => id === selectedCategoryIdsSorted[index]
      );

    if (allProductsSelected && !isAllProducts) {
      setIsAllProducts(true);
      form.setFieldValue('is_all_products', true);
    } else if (!allProductsSelected && isAllProducts) {
      setIsAllProducts(false);
      form.setFieldValue('is_all_products', false);
    }

    const currentQuantities = form.getFieldValue('product_quantities') || {};
    const filteredQuantities: Record<string | number, number> = {};

    selected.forEach((product) => {
      const qtyKey = product.id ?? product.category_id;
      if (qtyKey) {
        const quantity =
          (product.id ? currentQuantities[product.id] : undefined) ??
          (product.category_id
            ? currentQuantities[product.category_id]
            : undefined);
        if (quantity !== undefined) {
          filteredQuantities[qtyKey] = quantity;
        }
      }
    });

    if (isAllQuantities) {
      const unusedProducts =
        isEditMode && editingDelivery
          ? calculateUnusedProducts(editingDelivery)
          : calculateUnusedProducts();
      const newQuantities: Record<string | number, number> = {};

      selected.forEach((product) => {
        const qtyKey = product.id ?? product.category_id;
        if (qtyKey) {
          const unusedProduct = unusedProducts.find((unused) => {
            const productName =
              product.name ||
              product.category?.name ||
              `Product ${product.category_id}`;
            return unused.name === productName;
          });
          newQuantities[qtyKey] = unusedProduct?.quantity || 0;
        }
      });

      form.setFieldValue('product_quantities', newQuantities);
    } else {
      form.setFieldValue('product_quantities', filteredQuantities);
    }
  };

  const getFilteredAvailableProducts = useCallback(() => {
    if (isEditMode && editingDelivery) {
      return getAvailableProducts(editingDelivery);
    }
    return getAvailableProducts();
  }, [isEditMode, editingDelivery, getAvailableProducts]);

  const validateQuantities = (values: DetailedDeliveryFormValues) => {
    if (!values.product_quantities || selectedProducts.length === 0) {
      return true;
    }

    const unusedProducts =
      isEditMode && editingDelivery
        ? calculateUnusedProducts(editingDelivery)
        : calculateUnusedProducts();

    const fieldErrors: Array<{ name: (string | number)[]; errors: string[] }> =
      [];
    let hasErrors = false;

    for (const product of selectedProducts) {
      const qtyKey = product.id ?? product.category_id;
      const requestedQuantity = qtyKey
        ? values.product_quantities[qtyKey]
        : undefined;
      if (requestedQuantity && requestedQuantity > 0) {
        const productName =
          product.name ||
          product.category?.name ||
          `Product ${product.category_id}`;
        const unusedProduct = unusedProducts.find(
          (unused) => unused.name === productName
        );

        if (!unusedProduct || requestedQuantity > unusedProduct.quantity) {
          const availableQuantity = unusedProduct?.quantity || 0;
          const fieldKey = qtyKey;
          if (fieldKey) {
            fieldErrors.push({
              name: ['product_quantities', fieldKey],
              errors: [
                intl.formatMessage(
                  { id: 'validation.quantity_exceeds_available' },
                  {
                    available: availableQuantity,
                    unit: product.storage_unit?.short || 'kg',
                  }
                ),
              ],
            });
            hasErrors = true;
          }
        }
      }
    }

    if (hasErrors) {
      form.setFields(fieldErrors);
      return false;
    }

    return true;
  };

  const handleFormSubmit = (values: DetailedDeliveryFormValues) => {
    if (!values.delivery_address) {
      return;
    }

    if (!validateQuantities(values)) {
      return;
    }

    const getQtyValue = (product: ListingProduct) => {
      const pq = values.product_quantities;
      if (!pq) return undefined;
      return (
        pq[product.id!] ??
        pq[product.category_id] ??
        pq[String(product.id)] ??
        pq[String(product.category_id)]
      );
    };

    const productReferences = selectedProducts
      .filter((product) => {
        const val = getQtyValue(product);
        const qty = parseFloat(String(val ?? 0)) || 0;
        return qty > 0;
      })
      .map((product) => {
        const val = getQtyValue(product);
        const qty = parseFloat(String(val ?? 0)) || 0;
        return {
          listing_product_id: product.id,
          category_id: product.category_id,
          delivery_quantity: qty,
        };
      });

    const rawQuantities = values.product_quantities || {};
    const product_quantities: Record<string, number> = {};
    Object.entries(rawQuantities).forEach(([k, v]) => {
      if (v != null) {
        const num = parseFloat(String(v));
        if (!Number.isNaN(num) && num >= 0) {
          product_quantities[k] = num;
        }
      }
    });

    const deliveryData: DetailedDeliveryData = {
      ...(editingDelivery?.id && { id: editingDelivery.id }),
      delivery_address: values.delivery_address as string,
      delivery_start_date: values.delivery_start_date,
      delivery_end_date: values.delivery_end_date,
      is_all_products: values.is_all_products || false,
      is_full_quantity: values.is_full_quantity || false,
      selected_products: selectedProducts,
      product_quantities,
      products: productReferences,
      delivery_frequency_value: values.delivery_frequency_value,
      delivery_frequency_unit: values.delivery_frequency_unit,
      ...(splitDeliveriesMode?.enabled && { selectedApplicationId }),
    };

    let updatedDeliveries: DetailedDeliveryData[];

    if (isEditMode && editingDelivery) {
      const editIndex = deliveries.findIndex((d) => d === editingDelivery);
      if (editIndex !== -1) {
        updatedDeliveries = [...deliveries];
        updatedDeliveries[editIndex] = {
          ...editingDelivery,
          ...deliveryData,
        };
      } else {
        updatedDeliveries = [...deliveries, deliveryData];
      }
    } else {
      const newDelivery = {
        ...deliveryData,
      };
      updatedDeliveries = [...deliveries, newDelivery];
    }

    setDeliveries(updatedDeliveries);
    setShowAddForm(false);
    setIsEditMode(false);
    setEditingDelivery(null);
    onFormStateChange?.(false);
    onDeliveriesChange?.(updatedDeliveries);
    form.resetFields();
  };

  const handleFormCancel = () => {
    form.resetFields();
    setShowAddForm(false);
    setIsEditMode(false);
    setEditingDelivery(null);
    setSelectedProducts([]);
    setIsAllProducts(false);
    setIsAllQuantities(false);
    setSelectedApplicationId(null);
    onFormStateChange?.(false);
  };

  const handleOpenForm = () => {
    setShowAddForm(true);
    setIsEditMode(false);
    setEditingDelivery(null);
    setSelectedProducts([]);
    setIsAllProducts(false);
    setIsAllQuantities(false);
    setSelectedApplicationId(null);
    form.resetFields();
    onFormStateChange?.(true);
  };

  const handleEditDelivery = (deliveryIndex: number) => {
    const deliveryToEdit = deliveries[deliveryIndex];

    if (!deliveryToEdit) {
      return;
    }

    setEditingDelivery(deliveryToEdit);
    setIsEditMode(true);
    setShowAddForm(true);
    onFormStateChange?.(true);

    const reconstructedProducts = availableProducts.filter(
      (product) =>
        deliveryToEdit.selected_products?.some(
          (selectedProduct) =>
            selectedProduct.category_id === product.category_id ||
            (selectedProduct.id && selectedProduct.id === product.id)
        ) ||
        deliveryToEdit.products?.some(
          (productRef) =>
            (productRef.category_id &&
              productRef.category_id === product.category_id) ||
            (productRef.listing_product_id &&
              productRef.listing_product_id === product.id)
        )
    );

    setSelectedProducts(reconstructedProducts);
    setIsAllProducts(deliveryToEdit.is_all_products);
    setIsAllQuantities(deliveryToEdit.is_full_quantity);

    if (splitDeliveriesMode?.enabled) {
      setSelectedApplicationId(deliveryToEdit.selectedApplicationId ?? null);
    }

    const selectValue = reconstructedProducts
      .map((p) => ({
        value: p.category_id,
        label: p.name || p.category?.name || '',
      }))
      .filter((item) => item.value !== undefined && item.value !== null);

    const convertedProductQuantities: Record<string | number, number> = {};
    if (deliveryToEdit.product_quantities) {
      const pq = deliveryToEdit.product_quantities;
      reconstructedProducts.forEach((product) => {
        const quantity =
          pq[product.id!] ??
          pq[product.category_id] ??
          pq[String(product.id)] ??
          pq[String(product.category_id)];
        if (quantity !== undefined) {
          const qty = parseFloat(String(quantity)) || 0;
          if (qty > 0) {
            const formKey = product.category_id || product.id;
            if (formKey) {
              convertedProductQuantities[formKey] = qty;
            }
          }
        }
      });
    }

    form.setFieldsValue({
      delivery_address: deliveryToEdit.delivery_address,
      delivery_start_date: deliveryToEdit.delivery_start_date
        ? dayjs(deliveryToEdit.delivery_start_date)
        : undefined,
      delivery_end_date: deliveryToEdit.delivery_end_date
        ? dayjs(deliveryToEdit.delivery_end_date)
        : undefined,
      is_all_products: deliveryToEdit.is_all_products,
      is_full_quantity: deliveryToEdit.is_full_quantity,
      selected_products: selectValue,
      product_quantities: convertedProductQuantities,
      delivery_frequency_value: deliveryToEdit.delivery_frequency_value,
      delivery_frequency_unit: deliveryToEdit.delivery_frequency_unit,
    });
  };

  const handleDeleteDelivery = (deliveryIndex: number) => {
    if (deliveryIndex < 0 || deliveryIndex >= deliveries.length) {
      return;
    }

    const updatedDeliveries = deliveries.filter(
      (_, index) => index !== deliveryIndex
    );

    setDeliveries(updatedDeliveries);
    onDeliveriesChange?.(updatedDeliveries);
  };

  return (
    <StyledDetailedDeliveryForm className={showAddForm ? 'form-overlay' : ''}>
      <div className={'deliveries-items'}>
        {!showAddForm ? (
          <div className="cards-container">
            <DetailedDeliveryCard
              variant="add"
              onAdd={handleOpenForm}
              className={'add-delivery-card'}
              unusedProducts={calculateUnusedProducts(undefined, true)}
              availableProducts={availableProducts}
            />

            {deliveries.length > 0 &&
              deliveries.map((delivery, index) => (
                <DetailedDeliveryCard
                  key={delivery.id || `temp-${index}`}
                  variant="existing"
                  delivery={delivery}
                  deliveryNumber={index + 1}
                  onEdit={() => handleEditDelivery(index)}
                  onDelete={() => handleDeleteDelivery(index)}
                  availableProducts={availableProducts}
                />
              ))}
          </div>
        ) : (
          <div className="form">
            <Label
              title={true}
              label={intl.formatMessage({
                id: 'listings.add_detailed_delivery_title',
              })}
            />

            <DetailedDeliveryFormContent
              form={form}
              isEditMode={isEditMode}
              editingDelivery={editingDelivery}
              selectedProducts={selectedProducts}
              isAllProducts={isAllProducts}
              isAllQuantities={isAllQuantities}
              availableProducts={availableProducts}
              onAllProductsChange={handleAllProductsChange}
              onAllQuantitiesChange={handleAllQuantitiesChange}
              onProductSelectionChange={handleProductSelectionChange}
              onFormSubmit={handleFormSubmit}
              onFormCancel={handleFormCancel}
              getAvailableProducts={getFilteredAvailableProducts}
              calculateUnusedProducts={calculateUnusedProducts}
              splitDeliveriesMode={
                splitDeliveriesMode
                  ? {
                      ...splitDeliveriesMode,
                      selectedApplicationId,
                      onApplicationChange: handleApplicationChange,
                    }
                  : undefined
              }
            />
          </div>
        )}
      </div>
    </StyledDetailedDeliveryForm>
  );
};
